import { useFlag } from '@reflag/react-sdk';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ButtonGroup,
    type ButtonProps,
    DialogRoot,
    DialogTrigger,
} from '@gitbook/spine-react';
import { type LoadingStateHookResult, useLoadingState } from '@gitbook/util-react-hooks';

import { CreateAgentChangeRequestDialog, useCreateChangeRequest } from '../../../Components';
import { shortcuts, useKeyboardShortcuts } from '../../../Keyboard';

/**
 * Button to edit the space in a change request.
 */
export function EditSpaceButton(props: { space: api.Space }) {
    const { space } = props;
    const { isEnabled: withDocsAgents } = useFlag('DOCS_AGENTS');

    const loadable = useLoadingState();

    return withDocsAgents ? (
        <ButtonGroup kind="segmented" separatorKind="primary" disabled={loadable[0]} border={false}>
            <EditInCRButton space={space} loadable={loadable} />
            <ChangeRequestNewWithAgentButton space={space} />
        </ButtonGroup>
    ) : (
        <EditInCRButton space={space} loadable={loadable} />
    );
}

/**
 * Button to create a change request.
 */
function EditInCRButton(props: { space: api.Space; loadable: LoadingStateHookResult }) {
    const { space, loadable } = props;
    const [, wrapLoading] = loadable;
    const createChangeRequest = useCreateChangeRequest();

    useKeyboardShortcuts(
        {
            [shortcuts.EDIT_SPACE_CR]: () => {
                wrapLoading(async () => {
                    await createChangeRequest({
                        space,
                        source: 'space-keyboard-shortcut',
                    });
                });
            },
        },
        ['application']
    );

    return (
        <Button
            label="Edit"
            testID="space.editButton"
            leadingIcon={Icon.ChangeRequestNew}
            kind="primary"
            title="Edit content by starting a new change request"
            loadable={loadable}
            onPress={async () => {
                wrapLoading(async () => {
                    await createChangeRequest({
                        space,
                        source: 'space-edit-button',
                    });
                });
            }}
        />
    );
}

/**
 * Button to create a change request with the AI agent.
 */
function ChangeRequestNewWithAgentButton(
    props: Partial<ButtonProps> & {
        space: api.Space;
    }
) {
    const { space } = props;

    return (
        <DialogRoot>
            <DialogTrigger>
                <Button
                    label="Implement changes with Docs Agent"
                    iconOnly
                    leadingIcon={Icon.AI}
                    kind="primary"
                    {...props}
                />
            </DialogTrigger>
            <CreateAgentChangeRequestDialog spaceId={space.id} />
        </DialogRoot>
    );
}
